import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { defaultSideBarFilling } from '@/modules/auth/components/SignUpPagesWrapper.tsx';
import styles from '@/modules/entity/components/OnboardingNavigation.module.css';
import { useEntityItemUncachedQuery } from '@/modules/entity/queries/entity.tsx';
import { ShareholderDetails } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingRoadItem } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import { Spinner, Tooltip } from 'ui';

export interface OnboardingNavigationProps {
  roads?: OnboardingRoadItem[];
}

export const OnboardingNavigation = ({ roads }: OnboardingNavigationProps) => {
  const [params, setParams] = useSearchParams();

  const step = params.get('step');
  const [currentStep, setCurrentStep] = useState(step && !isNaN(+step) ? +step : 0);
  const { id: entity_id = 0 } = useParams();
  const { data, isLoading } = useEntityItemUncachedQuery(
    +entity_id,
    entity_id === 0 || isNaN(+entity_id),
  );

  const { navName: currentNavName } = roads?.[currentStep] ?? {};

  useEffect(() => {
    step && setCurrentStep(+step);
  }, [step]);

  const getSubStep = useCallback((step: number) => +(params.get(`sub_step${step}`) ?? 0), [params]);
  const getSubStepRoad = useCallback(
    (step: number, subStep: number) => +(params.get(`sub_step_road${step}_${subStep}`) ?? 0),
    [params],
  );
  const delSubStep = useCallback((step: number) => params.delete(`sub_step${step}`), [params]);
  const delSubStepRoad = useCallback(
    (step: number, subStep: number) => params.delete(`sub_step_road${step}_${subStep}`),
    [params],
  );

  const getPercentWithSubRoad = useCallback(
    (shareholders: ShareholderDetails[], step: number, allSubStep = 2) => {
      const filteredShareholders = shareholders?.filter(item => !item.same_as_index);
      if (!filteredShareholders || filteredShareholders?.length === 0) {
        return 100;
      }
      const countShareholders = (filteredShareholders?.length ?? 1) * allSubStep;
      const subStep = getSubStep(step);
      const subStepRoad = getSubStepRoad(step, subStep);
      const percent = ((subStep * allSubStep + (subStepRoad + 1)) / countShareholders) * 100;
      return percent;
    },
    [getSubStep, getSubStepRoad],
  );

  const goToPage = useCallback(
    (newStep: number) => {
      const currentStep = params.get('step') ?? '0';
      params.set('step', String(newStep));
      const subStep = getSubStep(+currentStep);
      delSubStepRoad(+currentStep, subStep);
      delSubStep(+currentStep);
      delSubStepRoad(newStep, 0);
      delSubStep(newStep);
      setCurrentStep(newStep);
      setParams(params, { replace: false });
    },
    [delSubStep, delSubStepRoad, getSubStep, params, setParams],
  );

  if (!currentNavName) {
    return defaultSideBarFilling;
  }

  return (
    <div
      className={classNames(
        'flex flex-col gap-2 m-0 self-start w-full',
        'font-[Inter] text-[16px] leading-6 overflow-y-auto max-h-[80%]',
        styles.scroll,
      )}
    >
      {isLoading ? (
        <div className="w-full flex items-center justify-center">
          <Spinner size="lg" variant="white" />
        </div>
      ) : (
        roads?.map((item, i) => {
          const { getNavProgress } = roads?.[i] ?? {};
          const currentProgress = getNavProgress({
            data,
            getSubStep,
            getPercentWithSubRoad,
            step: i,
          });
          return (
            <div
              className={classNames('flex gap-5 cursor-pointer group relative', {
                'min-h-9 h-9': !!item.navName,
                'h-0 !hidden': !item.navName,
              })}
              key={i}
              onClick={() => goToPage(i)}
            >
              <div className={classNames('relative w-0.5 bg-[rgba(255,_255,_255,_0.4)]')}>
                <div
                  className="absolute top-0 left-0 w-0.5 h-full bg-white"
                  style={{
                    height: currentNavName === item.navName ? `${currentProgress.percent}%` : '0%',
                  }}
                />
              </div>
              <Tooltip
                relativeBlock={false}
                delayTimeOpen={700}
                position="up"
                className="flex items-center"
                positionHorizontal="center"
                button={
                  <span
                    className={classNames(
                      'group-hover:underline',
                      'block text-center leading-9',
                      'max-w-[305px] min-w-0',
                      'overflow-hidden text-ellipsis whitespace-nowrap',
                      {
                        'line-through': currentProgress.completed,
                        'cursor-help': item.navName.length >= 38,
                      },
                    )}
                  >
                    <span>{item.navName}</span>
                  </span>
                }
                disabled={item.navName.length < 38}
              >
                <span className="">{item.navName}</span>
              </Tooltip>
            </div>
          );
        })
      )}
    </div>
  );
};
